import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import keycloak from '@/lib/keycloak'
import { getEnvVar, defaultEnv, ensureProtocol } from '@/lib/env'
import { ensureFreshKeycloakToken } from '@/lib/auth-token'

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null

// Default values for Apollo client
const FALLBACK_GRAPHQL_URL = 'http://localhost:3000/api/graphql/patient'
const FALLBACK_REALM = 'patients'

const authMiddleware = setContext(async (_operation, { headers }) => {
  try {
    const token = await ensureFreshKeycloakToken()
    const realm = getEnvVar('NEXT_PUBLIC_KEYCLOAK_REALM', defaultEnv.NEXT_PUBLIC_KEYCLOAK_REALM) || FALLBACK_REALM

    return {
      headers: {
        ...headers,
        'x-realm': realm,
        Authorization: token ? `Bearer ${token}` : '',
      },
    }
  } catch (e) {
    console.log(`[GraphQL authMiddleware error]: Message: ${JSON.stringify(e)}`)
    return headers
  }
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ extensions, message, locations, path }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      if (extensions && extensions.code === 'UNAUTHENTICATED') {
        if (keycloak.authenticated) {
          // Try to refresh the token before logging out
          keycloak.updateToken(30).catch(() => {
            // Token refresh failed — session is truly expired
            if (apolloClient) {
              apolloClient.clearStore()
            }
            keycloak.logout({ redirectUri: window.location.origin })
          })
        }
      }
    })
  if (networkError) console.log(`[Network error]: ${networkError}`)
})

const httpLink = new HttpLink({
  uri: () => {
    const rawGraphqlUrl = getEnvVar('NEXT_PUBLIC_GRAPHQL_API_URL', defaultEnv.NEXT_PUBLIC_GRAPHQL_API_URL)
    const processedGraphqlUrl = ensureProtocol(rawGraphqlUrl)
    const currentGraphqlUrl = processedGraphqlUrl || FALLBACK_GRAPHQL_URL
    return currentGraphqlUrl
  },
})

function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    cache: new InMemoryCache(),
    devtools: { enabled: true },
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
    },
    link: from([authMiddleware, errorLink, httpLink]),
    ssrMode: false,
  })
}

export const getApolloClient = (initialState: NormalizedCacheObject | null = null): ApolloClient<NormalizedCacheObject> => {
  const _apolloClient = apolloClient ?? createApolloClient()

  if (initialState) {
    _apolloClient.cache.restore(initialState)
  }

  // Create the Apollo Client once in the client
  apolloClient = apolloClient ?? _apolloClient

  return apolloClient
}
